import { inject, Injectable } from '@angular/core';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { combineLatest, filter, map, Observable, Subject, take, tap } from 'rxjs';

import { FirebaseEventUserInfoDto } from '@app/data/dtos/firebase-analytics/firebase-event-user-info.dto';
import { FirebaseEventType } from '@app/entities/enums/firebase-analytics/event-types.enum';
import { FeatureSelectedEventType } from '@app/entities/enums/firebase-analytics/feature-selected-analytics.enum';
import { MenuEventOriginValue } from '@app/entities/enums/firebase-analytics/menu-event-origin.enum';
import { ProfileMenuOptionEventType } from '@app/entities/enums/firebase-analytics/profile-menu-option-event-type.enum';
import { ProfileMenuOptionEventValue } from '@app/entities/enums/firebase-analytics/profile-menu-option-event.enum';
import { SalaryDocumentsEventValue } from '@app/entities/enums/firebase-analytics/salary-documents-event-value.enum';
import { ScreenNamePublicationAccess } from '@app/entities/enums/firebase-analytics/screen-name-publication-access.enum';
import { WorkingdayDocumentEventValue } from '@app/entities/enums/firebase-analytics/workday-documents-event-value.enum';
import { ENV } from '@environments/environment';

import { MPlatformService } from '@mercadona/core/platform';

import { FirebaseAnalyticsUseCases } from './firebase-analytics.use-cases.contract';
import { USER_USE_CASES, UserUseCases } from '../user/user.use-cases.contract';

import { AnnualCalendarEventValue } from '@/enums/firebase-analytics/annual-calendar-event-value.enum';
import { EvaluationAnalyticsSection } from '@/enums/firebase-analytics/evaluation-section.enum';

@Injectable()
export class FirebaseAnalyticsUseCasesImpl implements FirebaseAnalyticsUseCases {
  #angularFireAnalytics: AngularFireAnalytics = inject(AngularFireAnalytics);
  #userInfoService: UserUseCases = inject(USER_USE_CASES);
  #mPlatformService: MPlatformService = inject(MPlatformService);
  #logEvent: Subject<any> = new Subject();
  #logEventTrigger = combineLatest([this.#buildEventUserInfo(), this.#logEvent]).subscribe(([_eventUserInfo, event]) =>
    this.#angularFireAnalytics.logEvent(event.eventType, event.data)
  );

  logAppStartupEvent(): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_ROUTE,
      data: {
        type: 'home',
        value: 'acceso_activo2',
        debug_mode: ENV.firebaseDebugMode
      }
    });
  }

  logProfileMenuOptionClickEvent(value: ProfileMenuOptionEventValue): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_CLICK,
      data: {
        type: ProfileMenuOptionEventType.PROFILE,
        value,
        debug_mode: ENV.firebaseDebugMode
      }
    });
  }

  logMenuOptionClickEvent(type: FeatureSelectedEventType, value: MenuEventOriginValue): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_CLICK,
      data: {
        type,
        value,
        debug_mode: ENV.firebaseDebugMode
      }
    });
  }

  logPayslipDownloadEvent(monthYear: string): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_CLICK,
      data: {
        type: 'descarga_nomina',
        value: 'menu',
        month_year: monthYear,
        debug_mode: ENV.firebaseDebugMode
      }
    });
  }

  logSalaryDocumentDownloadEvent(value: SalaryDocumentsEventValue): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_CLICK,
      data: {
        type: 'documentos_salario',
        document_type: value,
        debug_mode: ENV.firebaseDebugMode
      }
    });
  }

  logWorkingdayDocumentDownloadEvent(value: WorkingdayDocumentEventValue): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_CLICK,
      data: {
        type: 'documentos_jornada',
        document_type: value,
        debug_mode: ENV.firebaseDebugMode
      }
    });
  }

  logPublicationAccessEvent(
    internalTitle: string,
    publicationId: string,
    screenName: ScreenNamePublicationAccess
  ): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_CLICK,
      data: {
        type: 'publicaciones',
        title_activo2: internalTitle,
        id_publication: publicationId,
        name_activo2: screenName,
        debug_mode: ENV.firebaseDebugMode
      }
    });
  }

  logPublicationInteractionEvent(internalTitle: string, publicationId: string): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_CLICK,
      data: {
        type: 'detalle_publicacion_video',
        internal_title: internalTitle,
        id_publication: publicationId,
        play_video: 'si'
      }
    });
  }

  logSearchClickEvent(value: string): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_INPUT,
      data: {
        type: 'buscador',
        value,
        debug_mode: ENV.firebaseDebugMode
      }
    });
  }

  logAnualCalendarTabClickEvent(value: AnnualCalendarEventValue): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_CLICK,
      data: {
        type: FeatureSelectedEventType.ANUAL_CALENDAR,
        value,
        debug_mode: ENV.firebaseDebugMode
      }
    });
  }

  logEvaluationDownloadEvent(value: EvaluationAnalyticsSection): void {
    this.#logEvent.next({
      eventType: FirebaseEventType.M_CLICK,
      data: {
        type: 'descarga_evaluacion',
        value,
        debug_mode: ENV.firebaseDebugMode
      }
    });
  }

  #buildEventUserInfo(): Observable<FirebaseEventUserInfoDto> {
    return this.#userInfoService.userInfoChanges$.pipe(
      filter((userInfo) => Boolean(userInfo)),
      take(1),
      map(
        (userInfo) =>
          ({
            m_username: userInfo.userid,
            m_device: 'web',
            m_app_version: ENV.appVersion,
            m_browser_version: String(this.#mPlatformService.getDeviceInfo().browser.version),
            m_user_department: userInfo.department,
            m_divisionzone: userInfo.division_zone,
            m_user_full_name: `${userInfo.name.concat(userInfo.lastname)}`,
            m_company: userInfo.companies.find((company) => Boolean(company.main_company))?.code,
            m_home_type: userInfo.home_type
          }) as FirebaseEventUserInfoDto
      ),
      tap((mappedUserInfo: FirebaseEventUserInfoDto) => {
        this.#angularFireAnalytics.setUserProperties(mappedUserInfo);
      })
    );
  }
}
